/**
 * senchado-collection-gift — ギフト棚ディスカバリー エンジン（G1）
 *
 * 設計: docs/spec-gift-redesign.md §G1 ＋ 2026-07-13 追記（着せ替え型）。
 * プロト: prototype/gift-shelf-mockup.html。
 * 道具エンジン(senchado-collection-dougu.js)の実証済みパターンを転用:
 *   ・Section Rendering ページング（paginate.pages 契約で残ページ取得＝全件を索引へ）
 *   ・在庫あり優先パーティション（売切は末尾）
 *   ・XSS防止の escapeHtml（innerHTML 生成箇所のみ）
 *
 * 道具との違い（本エンジン固有）:
 *   ・シーン = 着せ替え型（絞り込まない）。選択で (1)リード文 (2)推しコメントバッジ
 *     (3)並び順（該当品を前へ・元順は data-order で保持）を切り替える。母集団は常に全件。
 *   ・予算 = 絞り込み（単一選択トグル・解除可）。商品の実価格（data-price = cv.price / cents）
 *     から動的判定（メタフィールド不使用）。
 *   ・空状態は「予算×在庫」で0件のときのみ。予算クリア導線＋フォールバック3件
 *     （在庫あり上位・全商品から・シーン/予算を無視）を必ず出す。
 *   ・カート直結ボタンは持たない（プロト準拠のグリッド）。
 *
 * JSなしでも商品グリッドは Liquid 描画で見える（プログレッシブ）。本JSは発見面の上乗せ。
 */
(function () {
  'use strict';

  // ---------- ENGINE ----------
  function GiftEngine(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId || '';
    this.collectionHandle = root.dataset.collectionHandle || '';
    this.totalCount = parseInt(root.dataset.totalCount, 10) || 0;
    // ページ数はサーバー申告値（paginate.pages）が正。道具で実証済みのクランプ対策
    // （paginate by:50 でも page_size=20 にクランプされる → 50仮定は後半欠落事故）。
    this.totalPages = parseInt(root.dataset.pages, 10) || Math.ceil(this.totalCount / 50);

    this.grid = root.querySelector('[data-gift-grid]');
    this.emptyState = root.querySelector('[data-gift-empty]');
    this.fallbackWrap = root.querySelector('[data-gift-fallback]');
    this.loadingEl = root.querySelector('[data-gift-loading]');
    this.budgetRail = root.querySelector('[data-gift-budget-rail]');
    this.sceneRail = root.querySelector('[data-gift-scene-rail]');
    this.sceneLeadEl = root.querySelector('[data-gift-scene-lead]');
    this.visibleCountEl = root.querySelector('[data-gift-count]');
    this.availableOnlyEl = root.querySelector('[data-gift-available-only]');
    this.swatchRail = root.querySelector('[data-gift-swatches]');

    this.availableOnly = false;
    this.activeScene = null;   // シーンハンドル or null
    this.activeSceneLead = ''; // 選択中シーンのリード文
    this.activeSceneOshi = '';  // 選択中シーンの推しコメント文言
    this.activeBudget = null;  // {min, max} or null（min/max は円・max=0 は上限なし）
    this.defaultLead = this.sceneLeadEl ? this.sceneLeadEl.textContent : '';
    this.products = [];

    this.init();
  }

  GiftEngine.prototype.init = function () {
    var self = this;
    var rendered = this.grid ? this.grid.querySelectorAll('[data-gift-card]').length : 0;

    this.enhanceSwatches();
    this.start();

    // 全件が DOM に揃っていなければ残ページを取得（予算判定は全件が前提）。
    if (this.grid && rendered < this.totalCount) {
      this.root.dataset.backfill = 'loading';
      if (this.loadingEl) this.loadingEl.classList.add('is-visible');
      this.fetchRemainingPages().then(function () {
        if (self.loadingEl) self.loadingEl.classList.remove('is-visible');
        self.root.dataset.backfill = 'done';
      }).catch(function (err) {
        if (window.console) console.warn('[senchado-gift] 追加ページ取得失敗:', err);
        if (self.loadingEl) self.loadingEl.classList.remove('is-visible');
        self.root.dataset.backfill = 'error';
      });
    } else {
      this.root.dataset.backfill = 'done';
    }
  };

  GiftEngine.prototype.start = function () {
    this.buildIndex();
    this.recordOrder();
    // 初期の is-active シーンがあれば読み取る（Liquid が既定シーンを active 描画）。
    var active = this.sceneRail ? this.sceneRail.querySelector('[data-gift-scene].is-active') : null;
    if (active) {
      this.activeScene = active.dataset.giftScene || null;
      this.activeSceneLead = active.dataset.giftLead || '';
      this.activeSceneOshi = active.dataset.giftOshi || '';
    }
    this.bindEvents();
    this.applyView();
    this.root.dataset.engine = 'ready';
  };

  // ---------- INDEX ----------
  GiftEngine.prototype.buildIndex = function () {
    if (!this.grid) { this.products = []; return; }
    var cards = this.grid.querySelectorAll('[data-gift-card]');
    this.products = Array.prototype.map.call(cards, function (card) {
      return {
        el: card,
        price: parseInt(card.dataset.price, 10) || 0, // cents（cv.price）
        available: card.dataset.available === 'true',
        scenes: (card.dataset.scene || '').split('|').filter(Boolean),
        oshiEl: card.querySelector('[data-gift-oshi-badge]')
      };
    });
  };

  // 元の並び（Liquid 描画順）を data-order に固定。着せ替えの「残り」の順序を保つ基準。
  GiftEngine.prototype.recordOrder = function () {
    this.products.forEach(function (p, i) {
      if (p.el.dataset.order === undefined || p.el.dataset.order === '') {
        p.el.dataset.order = String(i);
      }
      p.order = parseInt(p.el.dataset.order, 10) || 0;
    });
  };

  // ---------- 判定 ----------
  GiftEngine.prototype.matchesBudget = function (p) {
    if (!this.activeBudget) return true;
    var yen = p.price / 100; // cv.price は cents → 円
    var min = this.activeBudget.min;
    var max = this.activeBudget.max;
    if (yen <= min) return false;         // 下限は排他（帯の重複を避ける）
    if (max > 0 && yen > max) return false; // 上限は包含（max=0 は上限なし）
    return true;
  };

  GiftEngine.prototype.matchesScene = function (p) {
    return !!this.activeScene && p.scenes.indexOf(this.activeScene) !== -1;
  };

  // ---------- 風呂敷スウォッチ: 画像フォールバック ----------
  // Liquid が商品画像ありなら img を描画済み。JS は「img が無いスウォッチだけ」色円に倒す
  // （data-gift-swatch-hex から）。JSなしでも Liquid の inline style で色円は出る（プログレッシブ）。
  GiftEngine.prototype.enhanceSwatches = function () {
    if (!this.swatchRail) return;
    var swatches = this.swatchRail.querySelectorAll('[data-gift-swatch]');
    Array.prototype.forEach.call(swatches, function (sw) {
      var dot = sw.querySelector('.senchado-gift__swatch-dot');
      if (!dot) return;
      if (dot.querySelector('img')) {
        dot.style.background = ''; // 画像あり → 背景色は不要
        return;
      }
      var hex = sw.dataset.giftSwatchHex || '';
      if (hex) dot.style.background = 'radial-gradient(circle at 32% 30%, ' + hex + ', ' + hex + ')';
    });
  };

  // ---------- VIEW（着せ替え＋絞り込み＋在庫優先の中央再描画） ----------
  GiftEngine.prototype.applyView = function () {
    var self = this;

    // リード文の着せ替え（シーン未選択なら既定文）
    if (this.sceneLeadEl) {
      this.sceneLeadEl.textContent = this.activeScene && this.activeSceneLead
        ? this.activeSceneLead
        : this.defaultLead;
    }

    // 推しコメントバッジのリセット＋付与（該当シーンの品のみ）。母集団は隠さない。
    this.products.forEach(function (p) {
      p.el.classList.remove('is-recommended');
      if (p.oshiEl) p.oshiEl.textContent = '';
    });

    // 絞り込み（予算＋在庫トグル）。シーンは絞り込みに一切関与しない。
    var visible = this.products.filter(function (p) {
      if (!self.matchesBudget(p)) return false;
      if (self.availableOnly && !p.available) return false;
      return true;
    });

    // 該当シーン品にバッジ付与（表示対象のうち）
    if (this.activeScene) {
      visible.forEach(function (p) {
        if (self.matchesScene(p)) {
          p.el.classList.add('is-recommended');
          if (p.oshiEl && self.activeSceneOshi) p.oshiEl.textContent = self.activeSceneOshi;
        }
      });
    }

    // 並び替え: 在庫あり優先（外側パーティション）→ シーン該当（内側で前へ）→ 元順(data-order)。
    var ordered = visible.slice().sort(function (a, b) {
      // 1) 在庫あり優先
      if (a.available !== b.available) return a.available ? -1 : 1;
      // 2) シーン該当を前へ
      var am = self.matchesScene(a) ? 0 : 1;
      var bm = self.matchesScene(b) ? 0 : 1;
      if (am !== bm) return am - bm;
      // 3) 元の並び
      return a.order - b.order;
    });

    // 全カードの表示/非表示（母集団のうち visible だけ出す）と order 付与。
    var shown = new Set(ordered);
    this.products.forEach(function (p) { p.el.classList.toggle('is-hidden', !shown.has(p)); });
    ordered.forEach(function (p, i) { p.el.style.order = String(i); });

    // 売切バッジ用クラス
    this.products.forEach(function (p) { p.el.classList.toggle('is-soldout', !p.available); });

    if (this.visibleCountEl) this.visibleCountEl.textContent = String(ordered.length);

    // 空状態（予算×在庫で0件のときのみ）。
    var isEmpty = ordered.length === 0;
    if (this.emptyState) this.emptyState.classList.toggle('is-visible', isEmpty);
    if (isEmpty) this.renderFallback();
  };

  // ---------- 空状態フォールバック（在庫あり上位3件・全商品から・シーン/予算無視） ----------
  GiftEngine.prototype.renderFallback = function () {
    if (!this.fallbackWrap) return;
    var top = this.products
      .filter(function (p) { return p.available; })
      .sort(function (a, b) { return a.order - b.order; })
      .slice(0, 3);
    this.fallbackWrap.textContent = '';
    top.forEach(function (p) {
      // 信頼済み DOM のクローン（Liquid が既にエスケープ済み）→ XSS 面なし。
      var clone = p.el.cloneNode(true);
      clone.classList.remove('is-hidden', 'is-recommended', 'is-soldout');
      clone.style.order = '';
      clone.removeAttribute('data-gift-card'); // 索引に混ざらないよう識別子を外す
      var badge = clone.querySelector('[data-gift-oshi-badge]');
      if (badge) badge.textContent = '';
      this.fallbackWrap.appendChild(clone);
    }, this);
  };

  // ---------- EVENTS ----------
  GiftEngine.prototype.bindEvents = function () {
    var self = this;

    if (this.availableOnlyEl) {
      this.availableOnlyEl.addEventListener('change', function () {
        self.availableOnly = !!self.availableOnlyEl.checked;
        self.applyView();
      });
    }

    this.root.addEventListener('click', function (e) {
      var t;
      if ((t = e.target.closest('[data-gift-scene]'))) {
        // 着せ替え: 同じシーン再クリックで解除、別シーンで切替。
        e.preventDefault();
        var handle = t.dataset.giftScene || '';
        var scenes = self.sceneRail ? self.sceneRail.querySelectorAll('[data-gift-scene]') : [];
        if (self.activeScene === handle) {
          self.activeScene = null;
          self.activeSceneLead = '';
          self.activeSceneOshi = '';
          Array.prototype.forEach.call(scenes, function (c) {
            c.classList.remove('is-active');
            c.setAttribute('aria-pressed', 'false');
          });
        } else {
          self.activeScene = handle;
          self.activeSceneLead = t.dataset.giftLead || '';
          self.activeSceneOshi = t.dataset.giftOshi || '';
          Array.prototype.forEach.call(scenes, function (c) {
            var on = c === t;
            c.classList.toggle('is-active', on);
            c.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
        }
        self.applyView();
      } else if ((t = e.target.closest('[data-gift-budget]'))) {
        // 絞り込み: 単一選択トグル（同じ帯再クリックで解除）。
        var min = parseInt(t.dataset.giftBudgetMin, 10) || 0;
        var max = parseInt(t.dataset.giftBudgetMax, 10) || 0;
        var same = self.activeBudget && self.activeBudget.min === min && self.activeBudget.max === max;
        var pills = self.budgetRail ? self.budgetRail.querySelectorAll('[data-gift-budget]') : [];
        if (same) {
          self.activeBudget = null;
          Array.prototype.forEach.call(pills, function (p) {
            p.classList.remove('is-active');
            p.setAttribute('aria-pressed', 'false');
          });
        } else {
          self.activeBudget = { min: min, max: max };
          Array.prototype.forEach.call(pills, function (p) {
            var on = p === t;
            p.classList.toggle('is-active', on);
            p.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
        }
        self.applyView();
      } else if ((t = e.target.closest('[data-gift-clear-budget]'))) {
        // 予算クリア導線（空状態）。
        e.preventDefault();
        self.activeBudget = null;
        var pills2 = self.budgetRail ? self.budgetRail.querySelectorAll('[data-gift-budget]') : [];
        Array.prototype.forEach.call(pills2, function (p) {
          p.classList.remove('is-active');
          p.setAttribute('aria-pressed', 'false');
        });
        self.applyView();
      }
    });
  };

  // ---------- Section Rendering ページング（道具から転用・全件を索引へ） ----------
  GiftEngine.prototype.fetchRemainingPages = function () {
    var self = this;
    var pages = this.totalPages;
    var nums = [];
    for (var p = 2; p <= pages; p++) nums.push(p);
    if (!nums.length) { this.rebuild(); return Promise.resolve(); }

    var failed = [];

    function fetchOnce(page) {
      return self.fetchPage(page).catch(function () { return ''; });
    }
    function handlePage(page, html) {
      if (html) self.appendPageCards(html);
      else failed.push(page);
    }

    var CONCURRENCY = 5;
    var idx = 0;
    function worker() {
      if (idx >= nums.length) return Promise.resolve();
      var page = nums[idx++];
      return fetchOnce(page).then(function (html) {
        handlePage(page, html);
        return worker();
      });
    }
    var pool = [];
    for (var w = 0; w < Math.min(CONCURRENCY, nums.length); w++) pool.push(worker());

    return Promise.all(pool).then(function () {
      if (!failed.length) return;
      var retry = failed.slice();
      failed = [];
      return retry.reduce(function (chain, page) {
        return chain.then(function () {
          return fetchOnce(page).then(function (html) {
            if (html) self.appendPageCards(html);
            else failed.push(page);
          });
        });
      }, Promise.resolve());
    }).then(function () {
      self.rebuild();
      var got = self.grid.querySelectorAll('[data-gift-card]').length;
      if (got < self.totalCount && window.console) {
        console.warn('[senchado-gift] 商品欠落の可能性: 表示 ' + got + ' / 申告 ' + self.totalCount +
          (failed.length ? '（再試行後も失敗ページ: ' + failed.join(',') + '）' : ''));
      }
    });
  };

  GiftEngine.prototype.appendPageCards = function (html) {
    if (!html || !this.grid) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var remoteRoot = doc.querySelector('[data-senchado-gift][data-section-id="' + this.sectionId + '"]') || doc;
    var cards = remoteRoot.querySelectorAll('[data-gift-grid] [data-gift-card]');
    if (!cards.length) return;
    var anchor = (this.emptyState && this.emptyState.parentNode === this.grid) ? this.emptyState : null;
    var frag = document.createDocumentFragment();
    Array.prototype.forEach.call(cards, function (card) { frag.appendChild(document.importNode(card, true)); });
    if (anchor) this.grid.insertBefore(frag, anchor);
    else this.grid.appendChild(frag);
  };

  GiftEngine.prototype.rebuild = function () {
    this.buildIndex();
    this.recordOrder();
    this.applyView();
  };

  GiftEngine.prototype.fetchPage = function (page) {
    var url = new URL(window.location.href);
    url.searchParams.set('section_id', this.sectionId);
    url.searchParams.set('page', String(page));
    return fetch(url.pathname + url.search).then(function (res) {
      return res.ok ? res.text() : '';
    });
  };

  // ---------- BOOT ----------
  function boot() {
    document.querySelectorAll('[data-senchado-gift]').forEach(function (root) {
      if (root.dataset.engineBooted) return;
      root.dataset.engineBooted = '1';
      try {
        new GiftEngine(root);
      } catch (err) {
        if (window.console) console.error('[senchado-gift]', err);
        root.dataset.engine = 'ready';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
